import { selector, selectorFamily } from "recoil";
import { unifiedCourseDataState } from "./unifiedCourseDataAtom";
import { smartSearchState, orderSmartResults } from "./smartSearchAtom";
import {
  getCourseIdentifier,
  lookupCourseRating,
} from "../helpers/courseUtils";

/**
 * Selector to get all course data for a specific semester
 */
export const semesterCourseDataSelector = selectorFamily({
  key: "semesterCourseDataSelector",
  get:
    (semesterShortName) =>
    ({ get }) => {
      const allCourseData = get(unifiedCourseDataState);
      return (
        allCourseData.semesters[semesterShortName] || {
          enrolledIds: [],
          available: [],
          selectedIds: [],
          filtered: [],
          studyPlan: [],
          ratings: {},
          lastFetched: null,
          isFutureSemester: false,
          referenceSemester: null,
          usingReferenceData: false,
          cisId: null,
          isCurrent: false,
          isProjected: false,
        }
      );
    },
});

/**
 * Selector to get the currently selected semester
 */
export const selectedSemesterSelector = selector({
  key: "selectedSemesterSelector",
  get: ({ get }) => {
    const courseData = get(unifiedCourseDataState);
    return courseData.selectedSemester;
  },
});

/**
 * Selector to get latest valid term
 */
export const latestValidTermSelector = selector({
  key: "latestValidTermSelector",
  get: ({ get }) => {
    const courseData = get(unifiedCourseDataState);
    return courseData.latestValidTerm;
  },
});

/**
 * Selector to get the current semester (marked with isCurrent: true)
 */
export const currentSemesterSelector = selector({
  key: "currentSemesterSelector",
  get: ({ get }) => {
    const allCourseData = get(unifiedCourseDataState);

    // Find the semester marked as current
    for (const [semesterShortName, semesterData] of Object.entries(
      allCourseData.semesters
    )) {
      if (semesterData.isCurrent) {
        return semesterShortName;
      }
    }

    // Fallback to latestValidTerm if no current semester found
    const fallback = allCourseData.latestValidTerm;
    if (fallback) {
      return fallback;
    }

    // Last fallback: return the first available semester
    const availableSemesters = Object.keys(allCourseData.semesters);
    if (availableSemesters.length > 0) {
      return availableSemesters[0];
    }

    // If absolutely nothing is available, return null
    return null;
  },
});

/**
 * Selector to get enrolled course IDs for a specific semester
 * Returns IDs instead of full course objects
 */
export const enrolledCoursesSelector = selectorFamily({
  key: "enrolledCoursesSelector",
  get:
    (semesterShortName) =>
    ({ get }) => {
      const semesterData = get(semesterCourseDataSelector(semesterShortName));
      return semesterData.enrolledIds || [];
    },
});

/**
 * Selector to get available courses for a specific semester
 */
export const availableCoursesSelector = selectorFamily({
  key: "availableCoursesSelector",
  get:
    (semesterShortName) =>
    ({ get }) => {
      const semesterData = get(semesterCourseDataSelector(semesterShortName));
      return semesterData.available || [];
    },
});

/**
 * Selector to get selected course IDs for a specific semester
 * Returns IDs instead of full course objects
 */
export const selectedCoursesSelector = selectorFamily({
  key: "selectedCoursesSelector",
  get:
    (semesterShortName) =>
    ({ get }) => {
      const semesterData = get(semesterCourseDataSelector(semesterShortName));
      return semesterData.selectedIds || [];
    },
});

/**
 * Selector to get course ratings for a specific semester
 */
export const semesterRatingsSelector = selectorFamily({
  key: "semesterRatingsSelector",
  get:
    (semesterShortName) =>
    ({ get }) => {
      const semesterData = get(semesterCourseDataSelector(semesterShortName));
      return semesterData.ratings || {};
    },
});

/**
 * Selector to get all semesters course data
 * Returns the complete unified course data structure
 */
export const allSemesterCoursesSelector = selector({
  key: "allSemesterCoursesSelector",
  get: ({ get }) => {
    const allCourseData = get(unifiedCourseDataState);
    return allCourseData.semesters;
  },
});

/**
 * Selector to check if a semester is marked as current
 */
export const semesterIsCurrentSelector = selectorFamily({
  key: "semesterIsCurrentSelector",
  get:
    (semesterShortName) =>
    ({ get }) => {
      const semesterData = get(semesterCourseDataSelector(semesterShortName));
      return semesterData.isCurrent || false;
    },
});

/**
 * Selector to check if a semester is projected
 */
export const semesterIsProjectedSelector = selectorFamily({
  key: "semesterIsProjectedSelector",
  get:
    (semesterShortName) =>
    ({ get }) => {
      const semesterData = get(semesterCourseDataSelector(semesterShortName));
      return semesterData.isProjected || false;
    },
});

/**
 * Selector to get semester metadata (isCurrent, isProjected, isFutureSemester, etc.)
 */
export const semesterMetadataSelector = selectorFamily({
  key: "semesterMetadataSelector",
  get:
    (semesterShortName) =>
    ({ get }) => {
      const semesterData = get(semesterCourseDataSelector(semesterShortName));
      return {
        isCurrent: semesterData.isCurrent || false,
        isProjected: semesterData.isProjected || false,
        isFutureSemester: semesterData.isFutureSemester || false,
        referenceSemester: semesterData.referenceSemester || null,
        // True when `available` is a borrowed previous-year preview (sparse/empty
        // current term). Consumers must treat these courses as belonging to
        // `referenceSemester`, not this semester. See REFERENCE_SEMESTER.md.
        usingReferenceData: semesterData.usingReferenceData || false,
        cisId: semesterData.cisId || null,
        lastFetched: semesterData.lastFetched || null,
      };
    },
});

/**
 * Selector to get courses for a specific semester by type (enrolled, available, selected, filtered)
 * This is the main selector used by EventListContainer
 */
export const semesterCoursesSelector = selectorFamily({
  key: "semesterCoursesSelector",
  get:
    ({ semester, type = "available" }) =>
    ({ get }) => {
      const semesterData = get(semesterCourseDataSelector(semester));

      // Handle different course types
      switch (type) {
        case "enrolled":
          // Derive full enrolled course objects from available + enrolledIds
          // Unified data stores only enrolledIds; 'enrolled' array is not populated
          {
            const enrolledIds = semesterData.enrolledIds || [];
            const availableCourses = semesterData.available || [];
            const selectedIds = semesterData.selectedIds || [];

            const enrolledCourses = availableCourses
              .filter((course) => {
                const courseNumber =
                  course.courses?.[0]?.courseNumber ||
                  course.courseNumber ||
                  course.id;
                return courseNumber && enrolledIds.includes(courseNumber);
              })
              .map((course) => {
                const courseNumber =
                  course.courses?.[0]?.courseNumber ||
                  course.courseNumber ||
                  course.id;
                return {
                  ...course,
                  enrolled: true,
                  // Preserve selected flag if this enrolled course is also selected
                  selected:
                    course.selected ||
                    (courseNumber && selectedIds.includes(courseNumber)) ||
                    false,
                };
              });

            if (import.meta?.env?.DEV) {
              // Minimal debug snapshot
              console.debug(
                "[semesterCoursesSelector] enrolled:",
                semester,
                {
                  enrolledIds: enrolledIds.length,
                  available: availableCourses.length,
                  result: enrolledCourses.length,
                }
              );
            }

            return enrolledCourses;
          }
        case "available":
          return semesterData.available || [];
        case "selected":
          // Filter available courses by selectedIds to get full course objects
          {
            const selectedIds = semesterData.selectedIds || [];
            const availableCourses = semesterData.available || [];
            const enrolledIds = semesterData.enrolledIds || [];

            const selectedCourses = availableCourses
              .filter((course) => {
                const courseNumber =
                  course.courses?.[0]?.courseNumber ||
                  course.courseNumber ||
                  course.id;
                return courseNumber && selectedIds.includes(courseNumber);
              })
              .map((course) => {
                const courseNumber =
                  course.courses?.[0]?.courseNumber ||
                  course.courseNumber ||
                  course.id;
                return {
                  ...course,
                  selected: true,
                  // Preserve enrolled flag if this selected course is also enrolled
                  enrolled:
                    course.enrolled ||
                    (courseNumber && enrolledIds.includes(courseNumber)) ||
                    false,
                };
              });

            if (import.meta?.env?.DEV) {
              console.debug(
                "[semesterCoursesSelector] selected:",
                semester,
                {
                  selectedIds: selectedIds.length,
                  available: availableCourses.length,
                  result: selectedCourses.length,
                }
              );
            }

            return selectedCourses;
          }
        case "filtered":
          // Return the actual filtered courses from the semester data
          return semesterData.filtered || [];
        default:
          return semesterData.available || [];
      }
    },
});

/**
 * The user's courses for a semester: enrolled ∪ selected, deduped by course
 * identifier (courseNumber, falling back to id).
 *
 * Single source of truth for "my courses" as *domain* state. It is deliberately
 * independent of the search panel's `filtered` pool, which is view state — a
 * course must not drop out of the schedule because a filter hides it from the
 * browsing list (see docs/BUG-calendar-entries-filter-leak.md).
 *
 * A course present in both lists appears once and keeps both flags.
 */
export const myCoursesSelector = selectorFamily({
  key: "myCoursesSelector",
  get:
    (semesterShortName) =>
    ({ get }) => {
      const enrolled = get(
        semesterCoursesSelector({
          semester: semesterShortName,
          type: "enrolled",
        })
      );
      const selected = get(
        semesterCoursesSelector({
          semester: semesterShortName,
          type: "selected",
        })
      );

      // Both pools already carry correct dual enrolled/selected flags
      // (each type cross-checks the other id list), so first-wins dedup
      // by identifier is sufficient.
      const byIdentifier = new Map();
      [...enrolled, ...selected].forEach((course) => {
        const identifier = getCourseIdentifier(course);
        if (!byIdentifier.has(identifier)) {
          byIdentifier.set(identifier, course);
        }
      });

      return Array.from(byIdentifier.values());
    },
});

/**
 * Selector to get the selected course info
 */
export const selectedCourseInfoSelector = selector({
  key: "selectedCourseInfoSelector",
  get: ({ get }) => {
    const courseData = get(unifiedCourseDataState);
    return courseData.selectedCourseInfo;
  },
});

/**
 * Selector to get CIS ID for a semester (handles projected semesters by using reference semester)
 */
export const semesterCisIdSelector = selectorFamily({
  key: "semesterCisIdSelector",
  get:
    (semesterShortName) =>
    ({ get }) => {
      const semesterData = get(semesterCourseDataSelector(semesterShortName));

      // For projected semesters, use the CIS ID from the reference semester
      if (semesterData.isProjected && semesterData.referenceSemester) {
        const referenceSemesterData = get(
          semesterCourseDataSelector(semesterData.referenceSemester)
        );
        return referenceSemesterData.cisId;
      }

      // For regular semesters, use their own CIS ID
      return semesterData.cisId;
    },
});

/**
 * Whether the course list should be showing smart-search results right now.
 *
 * A stored answer only applies to the semester it was asked about: the vector DB
 * returns bare ids, so resolving them against another term's catalog would show
 * that term's courses ranked by the old term's answer, with nothing on screen
 * saying so. Switching away therefore falls back to the keyword-filtered pool,
 * and switching back makes the answer valid again — no state is discarded,
 * because re-running the same query against the same term would return it.
 */
export const smartSearchActiveSelector = selector({
  key: "smartSearchActiveSelector",
  get: ({ get }) => {
    const search = get(smartSearchState);
    const { selectedSemester } = get(unifiedCourseDataState);
    return (
      search.mode === "smart" &&
      search.hasSearched &&
      search.semesterQueried === selectedSemester
    );
  },
});

/**
 * Smart (semantic) search results for the selected semester, best match first.
 *
 * The vector DB returns ids only, so they are resolved against the semester's
 * `available` courses. `available` carries no enrolled/selected flags (those are
 * attached to `filtered` by updateFilteredCourses), so they are re-attached here
 * — otherwise a wishlisted course would render with a "+" instead of its lock in
 * the shared EventListContainer row.
 */
export const smartSearchResultsSelector = selector({
  key: "smartSearchResultsSelector",
  get: ({ get }) => {
    const search = get(smartSearchState);
    const courseData = get(unifiedCourseDataState);
    const semester = courseData.selectedSemester;
    const semesterData = courseData.semesters?.[semester];
    const available = semesterData?.available || [];

    const ordered = orderSmartResults({
      resultIds: search.resultIds,
      distances: search.distances,
      courses: available,
    });

    const enrolledIds = semesterData?.enrolledIds || [];
    const selectedIds = semesterData?.selectedIds || [];
    const ratings = semesterData?.ratings || {};

    return ordered.map((course) => {
      const courseNumber = getCourseIdentifier(course);
      return {
        ...course,
        avgRating: lookupCourseRating(course, ratings) || course.avgRating,
        enrolled: !!courseNumber && enrolledIds.includes(courseNumber),
        selected: !!courseNumber && selectedIds.includes(courseNumber),
      };
    });
  },
});

/**
 * Selector to get semester abbreviation from selected course info
 * This replaces the logic that was using cisIdList
 */
export const selectedCourseSemesterSelector = selector({
  key: "selectedCourseSemesterSelector",
  get: ({ get }) => {
    const selectedCourse = get(selectedCourseInfoSelector);
    const allCourseData = get(unifiedCourseDataState);

    if (!selectedCourse || !selectedCourse.semesterId) {
      return null;
    }

    // Find the semester that matches the selected course's semesterId
    for (const [semesterShortName, semesterData] of Object.entries(
      allCourseData.semesters
    )) {
      if (semesterData.cisId === selectedCourse.semesterId) {
        return semesterShortName;
      }

      // Also check reference semester for projected semesters
      if (semesterData.isProjected && semesterData.referenceSemester) {
        const referenceSemesterData =
          allCourseData.semesters[semesterData.referenceSemester];
        if (
          referenceSemesterData &&
          referenceSemesterData.cisId === selectedCourse.semesterId
        ) {
          return semesterShortName;
        }
      }
    }

    return null;
  },
});
